import { useEffect } from "react";
import { Box, Divider, Typography } from "@mui/material";
import {
  APP_BACKGROUND_COLOR,
  APP_COLOR,
  BORDER_COLOR,
  FONT_FAMILY,
  TEXT_COLOR,
} from "../../theme";
import {
  legalDocumentCardSx,
  legalPageContainerSx,
  legalPageHeaderSx,
  legalPageMetaSx,
  legalPageShellSx,
  legalPageTitleSx,
} from "./styles";

interface Section {
  id: string;
  title: string;
  content: React.ReactNode;
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      color: "white",
      fontFamily: FONT_FAMILY,
      fontWeight: "bold",
      fontSize: { xs: "20px", md: "22px" },
      lineHeight: 1.4,
      mb: 1.5,
      mt: 0,
    }}
  >
    {children}
  </Typography>
);

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      color: TEXT_COLOR,
      fontFamily: FONT_FAMILY,
      fontSize: { xs: "15px", md: "16px" },
      lineHeight: 1.85,
      mb: 1.75,
    }}
  >
    {children}
  </Typography>
);

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      color: "white",
      fontFamily: FONT_FAMILY,
      fontWeight: "bold",
      fontSize: "15px",
      mt: 2,
      mb: 0.9,
    }}
  >
    {children}
  </Typography>
);

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <Box component="ul" sx={{ pl: 3, mt: 0.75, mb: 1.75 }}>
    {items.map((item) => (
      <Box
        key={item}
        component="li"
        sx={{
          color: TEXT_COLOR,
          fontFamily: FONT_FAMILY,
          fontSize: { xs: "15px", md: "16px" },
          lineHeight: 1.85,
          mb: 0.5,
        }}
      >
        {item}
      </Box>
    ))}
  </Box>
);

const CONTACT_EMAIL = "hello@ommuse.com";

const sections: Section[] = [
  {
    id: "1",
    title: "1. Introduction",
    content: (
      <>
        <Body>
          OmMuse, LLC ("OmMuse," "we," "us," or "our") is committed to
          protecting your privacy and handling your personal data in a
          transparent and secure manner. This Privacy Policy describes how we
          collect, use, disclose, and safeguard your information when you access
          or use the OmMuse platform, website, and related services
          (collectively, the "Platform").
        </Body>
        <Body>
          This Privacy Policy applies globally and is designed to comply with
          applicable data protection laws, including but not limited to:
        </Body>
        <BulletList
          items={[
            "The General Data Protection Regulation (GDPR) (EU)",
            "The UK GDPR",
            "The California Consumer Privacy Act (CCPA/CPRA)",
            "Other applicable U.S. and international privacy laws",
          ]}
        />
        <Body>
          By accessing or using the Platform, you acknowledge that you have read
          and understood this Privacy Policy.
        </Body>
      </>
    ),
  },
  {
    id: "2",
    title: "2. Information We Collect",
    content: (
      <>
        <Body>
          We collect information you provide directly, information collected
          automatically, and information from third parties.
        </Body>
        <SubHeading>A. Personal Information (PII)</SubHeading>
        <Body>We may collect the following:</Body>
        <BulletList
          items={[
            "Name, username, and profile details",
            "Email address and contact information",
            "Account login credentials",
            "Billing and payment information (processed by third-party providers)",
            "Creator-specific information (artist name, business details, uploaded content metadata)",
            "Communications with us (support requests, emails, etc.)",
          ]}
        />
        <SubHeading>B. Content and Collaboration Data</SubHeading>
        <Body>
          Given the nature of OmMuse, we may collect and store:
        </Body>
        <BulletList
          items={[
            "Audio files, stems, lyrics, artwork, and project files",
            "Collaboration activity and version history",
            "Metadata associated with uploaded content",
          ]}
        />
        <Box
          sx={{
            backgroundColor: `${APP_COLOR}12`,
            border: `1px solid ${APP_COLOR}35`,
            borderRadius: "8px",
            padding: "12px 16px",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              color: "white",
              fontFamily: FONT_FAMILY,
              fontSize: "13px",
              lineHeight: 1.7,
            }}
          >
            <strong>Important:</strong>{" "}
            <Typography component="span" sx={{ color: TEXT_COLOR, fontSize: "13px" }}>
              You retain ownership of your content. We process this data solely
              to provide and improve the Platform.
            </Typography>
          </Typography>
        </Box>
        <SubHeading>C. Automatically Collected Information (Non-PII)</SubHeading>
        <Body>We automatically collect:</Body>
        <BulletList
          items={[
            "IP address and approximate location",
            "Device and browser type",
            "Usage data and activity logs",
            "Referring URLs and session data",
            "Cookies and tracking data",
          ]}
        />
      </>
    ),
  },
  {
    id: "3",
    title: "3. How We Use Your Information",
    content: (
      <>
        <Body>We use your information to:</Body>
        <BulletList
          items={[
            "Provide, operate, and maintain the Platform",
            "Enable collaboration and content sharing",
            "Process transactions and manage subscriptions",
            "Improve platform performance and user experience",
            "Communicate with you (support, updates, service notices)",
            "Ensure security, fraud prevention, and compliance",
            "Comply with legal obligations",
          ]}
        />
        <Box
          sx={{
            backgroundColor: `${APP_COLOR}12`,
            border: `1px solid ${APP_COLOR}35`,
            borderRadius: "8px",
            padding: "12px 16px",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              color: "white",
              fontFamily: FONT_FAMILY,
              fontWeight: "bold",
              fontSize: "13px",
            }}
          >
            We do not sell your personal data.
          </Typography>
        </Box>
      </>
    ),
  },
  {
    id: "4",
    title: "4. Legal Bases for Processing (GDPR/UK GDPR)",
    content: (
      <>
        <Body>
          If you are located in the EEA or UK, we process your personal data
          based on:
        </Body>
        <BulletList
          items={[
            "Performance of a contract (providing the Platform)",
            "Legitimate interests (improving services, security)",
            "Consent (marketing communications, where applicable)",
            "Legal obligations",
          ]}
        />
      </>
    ),
  },
  {
    id: "5",
    title: "5. Sharing and Disclosure of Information",
    content: (
      <>
        <Body>We may share your information with:</Body>
        <SubHeading>A. Service Providers</SubHeading>
        <Body>
          Third parties who help operate our Platform, including:
        </Body>
        <BulletList
          items={[
            "Cloud storage providers",
            "Payment processors",
            "Analytics providers",
            "Security and infrastructure vendors",
          ]}
        />
        <Body>
          These providers are contractually obligated to protect your data.
        </Body>
        <SubHeading>B. Other Users</SubHeading>
        <Body>
          Certain information (e.g., profile, shared content) is visible to
          collaborators based on your permissions.
        </Body>
        <SubHeading>C. Legal and Compliance</SubHeading>
        <Body>We may disclose information:</Body>
        <BulletList
          items={[
            "To comply with legal obligations",
            "To enforce our Terms",
            "To protect rights, safety, and security",
          ]}
        />
        <SubHeading>D. Business Transfers</SubHeading>
        <Body>
          In connection with a merger, acquisition, or sale of assets, your
          information may be transferred.
        </Body>
      </>
    ),
  },
  {
    id: "6",
    title: "6. International Data Transfers",
    content: (
      <>
        <Body>
          Your information may be transferred to and processed in countries
          outside your jurisdiction, including the United States.
        </Body>
        <Body>Where required, we implement safeguards such as:</Body>
        <BulletList
          items={[
            "Standard Contractual Clauses (SCCs)",
            "Data processing agreements",
            "Equivalent legal mechanisms",
          ]}
        />
      </>
    ),
  },
  {
    id: "7",
    title: "7. Data Retention",
    content: (
      <>
        <Body>We retain your information:</Body>
        <BulletList
          items={[
            "As long as your account is active",
            "As necessary to provide services",
            "As required by legal, tax, or regulatory obligations",
          ]}
        />
        <Body>
          We may retain limited data after account deletion for compliance and
          dispute resolution.
        </Body>
      </>
    ),
  },
  {
    id: "8",
    title: "8. Your Privacy Rights",
    content: (
      <>
        <Body>
          Depending on your location, you may have the right to:
        </Body>
        <SubHeading>Global Rights</SubHeading>
        <BulletList
          items={[
            "Access your personal data",
            "Correct inaccurate data",
            "Delete your data",
            "Restrict or object to processing",
            "Data portability",
          ]}
        />
        <SubHeading>California (CCPA/CPRA) Rights</SubHeading>
        <BulletList
          items={[
            "Right to know what data is collected",
            "Right to delete personal data",
            'Right to opt out of "sale" (we do not sell data)',
            "Right to non-discrimination",
          ]}
        />
        <SubHeading>EEA/UK Rights</SubHeading>
        <BulletList
          items={[
            "Withdraw consent at any time",
            "Lodge a complaint with a supervisory authority",
          ]}
        />
        <Body>
          To exercise rights, contact:{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            style={{ color: APP_COLOR, textDecoration: "none" }}
          >
            {CONTACT_EMAIL}
          </a>
        </Body>
      </>
    ),
  },
  {
    id: "9",
    title: "9. Cookies and Tracking Technologies",
    content: (
      <>
        <Body>We use cookies and similar technologies to:</Body>
        <BulletList
          items={[
            "Authenticate users",
            "Improve functionality",
            "Analyze usage",
          ]}
        />
        <Body>
          You can control cookies through your browser settings.
        </Body>
      </>
    ),
  },
  {
    id: "10",
    title: "10. Data Security",
    content: (
      <>
        <Body>
          We implement reasonable administrative, technical, and physical
          safeguards, including:
        </Body>
        <BulletList
          items={[
            "Encryption (in transit and at rest where applicable)",
            "Access controls",
            "Secure cloud infrastructure",
          ]}
        />
        <Body>However, no system is completely secure.</Body>
      </>
    ),
  },
  {
    id: "11",
    title: "11. Children's Privacy",
    content: (
      <>
        <Body>
          The Platform is not intended for users under 13 years of age. We do
          not knowingly collect personal data from children.
        </Body>
        <Body>If we become aware of such data, we will delete it.</Body>
      </>
    ),
  },
  {
    id: "12",
    title: "12. Third-Party Services and Links",
    content: (
      <Body>
        The Platform may contain links to third-party services. We are not
        responsible for their privacy practices.
      </Body>
    ),
  },
  {
    id: "13",
    title: "13. Marketing Communications",
    content: (
      <>
        <Body>
          You may opt out of marketing communications at any time by:
        </Body>
        <BulletList
          items={[
            'Clicking "unsubscribe" in emails',
            `Contacting us at ${CONTACT_EMAIL}`,
          ]}
        />
      </>
    ),
  },
  {
    id: "14",
    title: "14. Changes to This Policy",
    content: (
      <>
        <Body>
          We may update this Privacy Policy periodically. Changes will be
          reflected by the "Last Updated" date.
        </Body>
        <Body>
          Continued use of the Platform constitutes acceptance of updates.
        </Body>
      </>
    ),
  },
  {
    id: "15",
    title: "15. Contact Information",
    content: (
      <>
        <Body>For questions or requests:</Body>
        <Body>
          <strong style={{ color: "white" }}>OmMuse, LLC</strong>
          <br />
          Email:{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            style={{ color: APP_COLOR, textDecoration: "none" }}
          >
            {CONTACT_EMAIL}
          </a>
        </Body>
      </>
    ),
  },
];

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    document.title = "Privacy Policy - OmMuse";
  }, []);

  return (
    <Box
      sx={{
        ...legalPageShellSx,
      }}
    >
      <Box sx={legalPageContainerSx}>
        <Box sx={legalPageHeaderSx}>
          <Typography sx={legalPageTitleSx}>
            OmMuse Privacy Policy
          </Typography>
          <Typography sx={legalPageMetaSx}>
            Last Updated: April 3, 2026
          </Typography>
        </Box>

        <Box sx={legalDocumentCardSx}>
          {sections.map((section, index) => (
            <Box key={section.id}>
              <Box id={section.id} sx={{ mb: 3 }}>
                <SectionTitle>{section.title}</SectionTitle>
                {section.content}
              </Box>
              {index < sections.length - 1 && (
                <Divider sx={{ borderColor: BORDER_COLOR, mb: 3 }} />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
